/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { AntflowApiEndpoint } from '@/services/dataprovider';
import { LauncherCreated } from '@/types/models';
import { TokenDisplay } from './TokenDisplay';
import { AntFlowServerError } from '@/services/dataprovider';

interface RotateLauncherDialogProps {
  visible: boolean;
  launcherName: string;
  onHide: () => void;
  onSuccess: () => void;
  apiEndpoint: AntflowApiEndpoint;
}

export const RotateLauncherDialog: React.FC<RotateLauncherDialogProps> = ({
  visible,
  launcherName,
  onHide,
  onSuccess,
  apiEndpoint
}) => {
  const { t } = useTranslation();
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [rotatedLauncher, setRotatedLauncher] = useState<LauncherCreated | null>(null);
  const [error, setError] = useState<string | null>();

  const rotateMutation = useMutation({
    mutationFn: async () => {
      const expDate = expirationDate ? expirationDate.toISOString() : undefined;
      return apiEndpoint.rotate_launcher_token(launcherName, expDate);
    },
    onSuccess: (data) => {
      setRotatedLauncher(data);
      onSuccess();
    },
    onError(error: AntFlowServerError) {
      const errorData = error.response?.data as Record<string, string>;
      if (typeof errorData === 'object' && errorData !== null) {
        setError(errorData.detail);
      }
    }
  });

  const handleClose = () => {
    setExpirationDate(null);
    setRotatedLauncher(null);
    setError(null);
    rotateMutation.reset();
    onHide();
  };

  const handleSubmit = () => {
    rotateMutation.mutate();
  };

  const footer = rotatedLauncher ? (
    <Button label={t(['actions.close', 'Close'])} onClick={handleClose} />
  ) : (
    <div className="flex justify-content-end gap-2">
      <Button
        label={t(['actions.cancel', 'Cancel'])}
        severity="secondary"
        onClick={handleClose}
        disabled={rotateMutation.isPending}
      />
      <Button
        label={t(['launchers.rotate', 'Rotate Token'])}
        icon="pi pi-refresh"
        onClick={handleSubmit}
        disabled={rotateMutation.isPending}
        loading={rotateMutation.isPending}
      />
    </div>
  );

  return (
    <Dialog
      header={t(['launchers.rotate_title', 'Rotate Launcher Token'])}
      visible={visible}
      style={{ width: '40%' }}
      onHide={handleClose}
      footer={footer}
      closable={!rotateMutation.isPending}
    >
      {rotatedLauncher ? (
        <div className="flex flex-column gap-3">
          <div className="p-message p-message-success p-3 border-round">
            <i className="pi pi-check-circle mr-2" />
            {t(['launchers.rotated_success', 'Token rotated successfully!'])}
          </div>
          <TokenDisplay token={rotatedLauncher.token} label={t(['launchers.new_token', 'New Token'])} />
        </div>
      ) : (
        <div className="flex flex-column gap-3">
          <p className="m-0">
            {t(['launchers.rotate_confirm', 'Generate a new token for launcher:'])} <strong>{launcherName}</strong>
          </p>
          <div className="p-message p-message-warn p-3 border-round">
            <i className="pi pi-exclamation-triangle mr-2" />
            {t(['launchers.rotate_warning', 'The current token will be invalidated immediately.'])}
          </div>

          <div className="flex flex-column gap-2">
            <label htmlFor="expiration-date" className="font-medium">
              {t(['launchers.new_expiration_date', 'New expiration date'])}
            </label>
            <Calendar
              id="expiration-date"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.value as Date | null)}
              showIcon
              minDate={new Date()}
              placeholder={t(['launchers.expiration_placeholder', 'Optional'])}
              disabled={rotateMutation.isPending}
            />
          </div>

          {rotateMutation.isError && (
            <div className="p-message p-message-error p-3 border-round">
              <i className="pi pi-times-circle mr-2" />
              {error || t(['notifs.error.description', 'An error occurred'])}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};
